<template>
  <div class="email-builder">
    <!-- Panel lateral izquierdo -->
    <div class="sidebar-panel">
      <!-- Tabs de navegación -->
      <div class="sidebar-tabs">
        <button 
          :class="['tab-btn', { active: activeTab === 'components' }]"
          @click="activeTab = 'components'"
        >
          <i class="fas fa-puzzle-piece"></i>
          <span>Componentes</span>
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'templates' }]"
          @click="activeTab = 'templates'"
        >
          <i class="fas fa-layer-group"></i>
          <span>Plantillas</span>
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'settings' }]"
          @click="activeTab = 'settings'"
        >
          <i class="fas fa-cog"></i>
          <span>Configuración</span>
        </button>
      </div>

      <!-- Contenido de tabs -->
      <div class="sidebar-content">
        <!-- Tab de Componentes -->
        <div v-if="activeTab === 'components'" class="components-panel">
          <div class="search-box">
            <input 
              type="text" 
              v-model="componentSearch"
              placeholder="Buscar componentes..."
              class="search-input"
            >
            <i class="fas fa-search search-icon"></i>
          </div>
          
          <div class="components-categories">
            <button 
              v-for="category in componentCategories" 
              :key="category.id"
              :class="['category-btn', { active: selectedCategory === category.id }]"
              @click="selectedCategory = category.id"
            >
              {{ category.name }}
            </button>
          </div>

          <div class="components-list">
            <div 
              v-for="component in filteredComponents" 
              :key="component.id"
              class="component-item"
              draggable="true"
              @dragstart="onDragStart($event, component)"
              @dragend="onDragEnd"
              :title="component.description"
            >
              <div class="component-icon">
                <i :class="component.icon"></i>
              </div>
              <div class="component-info">
                <span class="component-name">{{ component.name }}</span>
                <span class="component-desc">{{ component.description }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab de Plantillas -->
        <div v-if="activeTab === 'templates'" class="templates-panel">
          <div class="templates-header">
            <h3>Plantillas</h3>
            <div class="templates-actions">
              <button @click="showSaveTemplateModal = true" class="btn-save-template">
                <i class="fas fa-save"></i>
              </button>
              <button @click="loadTemplateModal = true" class="btn-load-template">
                <i class="fas fa-folder-open"></i>
              </button>
            </div>
          </div>
          
          <div class="template-search">
            <input 
              type="text" 
              v-model="templateSearch"
              placeholder="Buscar plantillas..."
              class="search-input"
            >
          </div>

          <div class="template-categories">
            <button 
              v-for="category in templateCategories" 
              :key="category"
              :class="['category-btn', { active: selectedTemplateCategory === category }]"
              @click="selectedTemplateCategory = category"
            >
              {{ category }}
            </button>
          </div>

          <div class="templates-grid">
            <div 
              v-for="template in filteredTemplates" 
              :key="template.id"
              class="template-card"
              @click="loadTemplate(template)"
            >
              <div class="template-thumbnail">
                <img :src="template.thumbnail" :alt="template.name" />
                <div class="template-overlay">
                  <button class="btn-use-template">
                    <i class="fas fa-plus"></i>
                    Usar
                  </button>
                </div>
              </div>
              <div class="template-info">
                <h4>{{ template.name }}</h4>
                <p>{{ template.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab de Configuración -->
        <div v-if="activeTab === 'settings'" class="settings-panel">
          <h3>Configuración del Email</h3>
          
          <div class="setting-group">
            <label>Ancho del Email</label>
            <select v-model="emailSettings.width" class="setting-select">
              <option value="600px">600px (Estándar)</option>
              <option value="800px">800px (Ancho)</option>
              <option value="100%">100% (Responsive)</option>
            </select>
          </div>

          <div class="setting-group">
            <label>Color de Fondo</label>
            <input type="color" v-model="emailSettings.backgroundColor" class="setting-color">
          </div>

          <div class="setting-group">
            <label>Familia de Fuente</label>
            <select v-model="emailSettings.fontFamily" class="setting-select">
              <option value="Arial, sans-serif">Arial</option>
              <option value="Helvetica, sans-serif">Helvetica</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
            </select>
          </div>

          <div class="setting-group">
            <label>Variables Personalizadas</label>
            <div class="variables-list">
              <div v-for="(variable, index) in customVariables" :key="index" class="variable-item">
                <input v-model="variable.name" placeholder="Nombre" class="variable-input">
                <input v-model="variable.value" placeholder="Valor" class="variable-input">
                <button @click="removeVariable(index)" class="btn-remove">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <button @click="addVariable" class="btn-add-variable">
                <i class="fas fa-plus"></i> Agregar Variable
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Área principal de trabajo -->
    <div class="main-workspace">
      <!-- Toolbar superior -->
      <div class="top-toolbar">
        <div class="toolbar-left">
          <h2 class="project-title">{{ projectName || 'Email Sin Título' }}</h2>
          <button @click="showProjectModal = true" class="btn-edit-name">
            <i class="fas fa-edit"></i>
          </button>
        </div>
        
        <div class="toolbar-center">
          <div class="view-mode-selector">
            <button 
              :class="['view-btn', { active: viewMode === 'desktop' }]"
              @click="viewMode = 'desktop'"
              title="Vista Desktop"
            >
              <i class="fas fa-desktop"></i>
            </button>
            <button 
              :class="['view-btn', { active: viewMode === 'tablet' }]"
              @click="viewMode = 'tablet'"
              title="Vista Tablet"
            >
              <i class="fas fa-tablet-alt"></i>
            </button>
            <button 
              :class="['view-btn', { active: viewMode === 'mobile' }]"
              @click="viewMode = 'mobile'"
              title="Vista Mobile"
            >
              <i class="fas fa-mobile-alt"></i>
            </button>
          </div>
        </div>

        <div class="toolbar-right">
          <div class="action-buttons">
            <button @click="undoAction" :disabled="!canUndo" class="btn-action" title="Deshacer">
              <i class="fas fa-undo"></i>
            </button>
            <button @click="redoAction" :disabled="!canRedo" class="btn-action" title="Rehacer">
              <i class="fas fa-redo"></i>
            </button>
            <div class="divider"></div>
            <button @click="previewEmail" class="btn-preview">
              <i class="fas fa-eye"></i> Vista Previa
            </button>
            <button @click="exportHTML" class="btn-export">
              <i class="fas fa-code"></i> Exportar
            </button>
            <button @click="showEmailCampaignModal = true" class="btn-send">
              <i class="fas fa-paper-plane"></i> Enviar
            </button>
          </div>
        </div>
      </div>

      <!-- Área de diseño -->
      <div class="design-container" :class="`view-${viewMode}`">
        <div class="design-wrapper">
          <div 
            class="design-canvas"
            :style="canvasStyles"
            @dragover.prevent
            @dragenter.prevent="onDragEnter"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <!-- Email Header/Footer indicadores -->
            <div class="email-bounds-indicator email-start">
              <span>Inicio del Email</span>
            </div>



            <!-- Bloques del email -->
            <div v-if="template.length === 0" class="empty-canvas">
              <div class="empty-message">
                <i class="fas fa-plus-circle"></i>
                <h3>Comienza a construir tu email</h3>
                <p>Arrastra componentes desde el panel lateral o selecciona una plantilla predefinida</p>
              </div>
            </div>

            <!-- Renderizado directo de bloques -->
            <div v-else class="blocks-container">
              <div 
                v-for="(block, index) in template" 
                :key="block.id"
                class="design-block"
                :class="{ 
                  'block-selected': selectedBlockIndex === index,
                  'block-hover': hoverBlockIndex === index 
                }"
                @mouseenter="hoverBlockIndex = index"
                @mouseleave="hoverBlockIndex = null"
                @click="selectBlock(index)"
              >
                <!-- Block Toolbar -->
                <div class="block-toolbar" v-show="selectedBlockIndex === index || hoverBlockIndex === index">
                  <div class="block-handle">
                    <i class="fas fa-grip-vertical"></i>
                  </div>
                  <div class="block-info">
                    <span class="block-type">{{ getBlockDisplayName(block.type) }}</span>
                  </div>
                  <div class="block-actions">
                    <button @click.stop="editBlock(index)" class="btn-action" title="Editar">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button @click.stop="duplicateBlock(index)" class="btn-action" title="Duplicar">
                      <i class="fas fa-copy"></i>
                    </button>
                    <button @click.stop="moveBlockUp(index)" :disabled="index === 0" class="btn-action" title="Subir">
                      <i class="fas fa-arrow-up"></i>
                    </button>
                    <button @click.stop="moveBlockDown(index)" :disabled="index === template.length - 1" class="btn-action" title="Bajar">
                      <i class="fas fa-arrow-down"></i>
                    </button>
                    <button @click.stop="removeBlock(index)" class="btn-action btn-delete" title="Eliminar">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>

                <!-- Block Content -->
                <div class="block-content" @dblclick="editBlock(index)">
                  <component 
                    :is="block.type"
                    v-bind="block.props"
                    :content="block.content"
                  />
                </div>

                <!-- Block Insert Areas -->
                <div 
                  class="insert-area insert-above" 
                  @dragover.prevent
                  @drop.prevent="onDropAtPosition($event, index)"
                >
                  <div class="insert-indicator">
                    <i class="fas fa-plus"></i>
                    <span>Insertar aquí</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Insert area at bottom -->
            <div 
              v-if="template.length > 0"
              class="insert-area insert-bottom" 
              @dragover.prevent
              @drop.prevent="onDropAtPosition($event, template.length)"
            >
              <div class="insert-indicator">
                <i class="fas fa-plus"></i>
                <span>Insertar al final</span>
              </div>
            </div>

            <div class="email-bounds-indicator email-end">
              <span>Fin del Email</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Editor Modal -->
    <modal 
      v-if="showEditModal"
      :title="getModalTitle"
      :component-type="currentBlock && currentBlock.type"
      :initial-content="currentBlock && currentBlock.content"
      :initial-styles="currentBlock && currentBlock.props"
      @close="closeModal"
      @save="saveBlockChanges"
    >
    </modal>
  </div>
</template>

<script>
// import draggable from 'vuedraggable'
import { QuillEditor } from 'vue-quill-editor'
import Modal from './components/Modal.vue'
import TemplateService from './services/templateService.js'
import 'quill/dist/quill.snow.css'

// Componentes de bloques
import TextBlock from './blocks/TextBlock.vue'
import ImageBlock from './blocks/ImageBlock.vue'
import ButtonBlock from './blocks/ButtonBlock.vue'
import SpacerBlock from './blocks/SpacerBlock.vue'
import DividerBlock from './blocks/DividerBlock.vue'
import ColumnBlock from './blocks/ColumnBlock.vue'
import SocialBlock from './blocks/SocialBlock.vue'
import TableBlock from './blocks/TableBlock.vue'
import VideoBlock from './blocks/VideoBlock.vue'

export default {
  name: 'EmailBuilder',
  
  components: {
    // draggable,
    QuillEditor,
    Modal,
    TextBlock,
    ImageBlock,
    ButtonBlock,
    SpacerBlock,
    DividerBlock,
    ColumnBlock,
    SocialBlock,
    TableBlock,
    VideoBlock
  },

  data() {
    return {
      // UI State
      activeTab: 'components',
      viewMode: 'desktop',
      selectedBlockIndex: null,
      hoverBlockIndex: null,
      isDraggingBlock: false,
      
      // Project
      projectName: '',
      showProjectModal: false,
      
      // Templates
      showSaveTemplateModal: false,
      loadTemplateModal: false,
      templateSearch: '',
      selectedTemplateCategory: 'all',
      availableTemplates: { saved: [], predefined: [] },
      
      // Components
      componentSearch: '',
      selectedCategory: 'all',
      componentCategories: [
        { id: 'all', name: 'Todos' },
        { id: 'basic', name: 'Básicos' },
        { id: 'layout', name: 'Layout' },
        { id: 'media', name: 'Media' },
        { id: 'social', name: 'Social' },
        { id: 'advanced', name: 'Avanzados' }
      ],
      availableComponents: [
        // Básicos
        {
          id: 'text',
          name: 'Texto',
          description: 'Párrafos y títulos con formato',
          type: 'TextBlock',
          icon: 'fas fa-paragraph',
          category: 'basic',
          content: '<p>Ingrese su texto aquí...</p>',
          props: { 
            fontSize: '16px', 
            color: '#2c3e50',
            backgroundColor: 'transparent',
            textAlign: 'left',
            fontFamily: 'Arial, sans-serif',
            fontWeight: '400',
            lineHeight: '1.5',
            padding: '16px',
            margin: '10px 0',
            borderRadius: '0px',
            border: 'none'
          }
        },
        {
          id: 'image',
          name: 'Imagen',
          description: 'Imágenes y fotografías',
          type: 'ImageBlock',
          icon: 'fas fa-image',
          category: 'basic',
          content: 'https://via.placeholder.com/600x200/3b82f6/ffffff?text=Imagen+de+Ejemplo',
          props: { 
            width: '100%',
            height: 'auto',
            alt: 'Imagen',
            align: 'center',
            caption: '',
            captionAlign: 'center',
            captionColor: '#6c757d',
            captionSize: '14px',
            borderRadius: '0px',
            border: 'none',
            padding: '10px',
            margin: '10px 0',
            backgroundColor: 'transparent',
            objectFit: 'cover',
            linkUrl: ''
          }
        },
        {
          id: 'button',
          name: 'Botón',
          description: 'Botones de llamada a la acción',
          type: 'ButtonBlock',
          icon: 'fas fa-mouse-pointer',
          category: 'basic',
          content: { text: 'Haz clic aquí', url: '#', styles: { backgroundColor: '#007bff', color: '#ffffff', align: 'center' } },
          props: {}
        },
        {
          id: 'spacer',
          name: 'Espaciador',
          description: 'Espacio en blanco vertical',
          type: 'SpacerBlock',
          icon: 'fas fa-arrows-alt-v',
          category: 'basic',
          content: '',
          props: { 
            height: '40px',
            backgroundColor: 'transparent',
            borderTop: 'none',
            borderBottom: 'none',
            margin: '0',
            showGuides: true,
            isEditing: false
          }
        },
        {
          id: 'divider',
          name: 'Divisor',
          description: 'Línea horizontal separadora',
          type: 'DividerBlock',
          icon: 'fas fa-minus',
          category: 'basic',
          content: '',
          props: { 
            color: '#e2e8f0',
            style: 'solid',
            width: '100%',
            thickness: '1px',
            align: 'center',
            margin: '20px 0',
            padding: '0',
            backgroundColor: 'transparent',
            borderRadius: '0px',
            opacity: '1'
          }
        },
        // Layout
        {
          id: 'columns',
          name: 'Columnas',
          description: 'Layout de 2, 3 o 4 columnas',
          type: 'ColumnBlock',
          icon: 'fas fa-columns',
          category: 'layout',
          content: [{ content: '' }, { content: '' }],
          props: { columns: 2, gap: '20px', padding: '20px' }
        },
        // Media
        {
          id: 'video',
          name: 'Video',
          description: 'Video con imagen de portada',
          type: 'VideoBlock',
          icon: 'fas fa-play-circle',
          category: 'media',
          content: '',
          props: { 
            videoUrl: '#',
            thumbnailUrl: '',
            title: 'Ver Video',
            description: 'Descripción del video'
          }
        },
        // Social
        {
          id: 'social',
          name: 'Redes Sociales',
          description: 'Iconos de redes sociales',
          type: 'SocialBlock',
          icon: 'fas fa-share-alt',
          category: 'social',
          content: '',
          props: {
            title: '¡Síguenos!',
            socialLinks: [
              { name: 'Facebook', icon: 'fab fa-facebook-f', url: '#', color: '#1877f2' },
              { name: 'Twitter', icon: 'fab fa-twitter', url: '#', color: '#1da1f2' },
              { name: 'Instagram', icon: 'fab fa-instagram', url: '#', color: '#e4405f' }
            ]
          }
        },
        // Avanzados
        {
          id: 'table',
          name: 'Tabla',
          description: 'Tabla de datos estructurados',
          type: 'TableBlock',
          icon: 'fas fa-table',
          category: 'advanced',
          content: '',
          props: {
            headers: ['Columna 1', 'Columna 2', 'Columna 3'],
            rows: [
              ['Dato 1', 'Dato 2', 'Dato 3'],
              ['Dato A', 'Dato B', 'Dato C']
            ],
            showHeader: true
          }
        }
      ],
      
      // Email Template
      template: [],
      
      // Settings
      emailSettings: {
        width: '600px',
        backgroundColor: '#ffffff',
        fontFamily: 'Arial, sans-serif'
      },
      customVariables: [
        { name: 'nombre', value: 'Usuario' },
        { name: 'empresa', value: 'Mi Empresa' }
      ],
      
      // Modals
      showEditModal: false,
      showEmailCampaignModal: false,
      currentBlockIndex: null,
      
      // History (Undo/Redo)
      history: [],
      historyIndex: -1,
      maxHistorySize: 50,
      
      // Drag & Drop
      draggedItem: null,
      isDragging: false,
      
      // Templates
      templateCategories: ['all', 'newsletter', 'promocional', 'bienvenida', 'evento', 'custom'],
      
      editorOptions: {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'script': 'sub'}, { 'script': 'super' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['link', 'image']
          ]
        }
      }
    }
  },

  computed: {
    currentBlock() {
      return this.currentBlockIndex !== null ? this.template[this.currentBlockIndex] : null
    },

    filteredComponents() {
      let components = this.availableComponents
      
      // Filtrar por categoría
      if (this.selectedCategory !== 'all') {
        components = components.filter(c => c.category === this.selectedCategory)
      }
      
      // Filtrar por búsqueda
      if (this.componentSearch.trim()) {
        const search = this.componentSearch.toLowerCase()
        components = components.filter(c => 
          c.name.toLowerCase().includes(search) ||
          c.description.toLowerCase().includes(search)
        )
      }
      
      return components
    },

    filteredTemplates() {
      const templates = [
        ...this.availableTemplates.predefined,
        ...this.availableTemplates.saved
      ]
      
      let filtered = templates
      
      // Filtrar por categoría
      if (this.selectedTemplateCategory !== 'all') {
        filtered = filtered.filter(t => t.category === this.selectedTemplateCategory)
      }
      
      // Filtrar por búsqueda
      if (this.templateSearch.trim()) {
        const search = this.templateSearch.toLowerCase()
        filtered = filtered.filter(t => 
          t.name.toLowerCase().includes(search) ||
          t.description.toLowerCase().includes(search)
        )
      }
      
      return filtered
    },

    canvasStyles() {
      const styles = {
        fontFamily: this.emailSettings.fontFamily,
        backgroundColor: this.emailSettings.backgroundColor
      }
      
      // Ajustar ancho según el modo de vista
      switch (this.viewMode) {
        case 'desktop':
          styles.width = this.emailSettings.width
          styles.maxWidth = '800px'
          break
        case 'tablet':
          styles.width = '768px'
          styles.maxWidth = '768px'
          break
        case 'mobile':
          styles.width = '375px'
          styles.maxWidth = '375px'
          break
      }
      
      return styles
    },

    canUndo() {
      return this.historyIndex > 0
    },

    canRedo() {
      return this.historyIndex < this.history.length - 1
    },

    getModalTitle() {
      if (!this.currentBlock) return 'Editar Componente'
      
      const titles = {
        TextBlock: 'Editar Texto',
        ImageBlock: 'Editar Imagen',
        ButtonBlock: 'Editar Botón',
        SpacerBlock: 'Editar Espaciador',
        DividerBlock: 'Editar Divisor',
        ColumnBlock: 'Editar Columnas',
        SocialBlock: 'Editar Redes Sociales',
        TableBlock: 'Editar Tabla',
        VideoBlock: 'Editar Video'
      }
      
      return titles[this.currentBlock.type] || 'Editar Componente'
    }
  },

  mounted() {
    // Cargar plantillas al inicializar el componente
    this.loadTemplates()
    
    // Inicializar historial con estado vacío
    this.saveToHistory()
    
    // Event listeners para teclado (Ctrl+Z, Ctrl+Y)
    document.addEventListener('keydown', this.handleKeyboard)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyboard)
  },

  methods: {
    onDragStart(event, component) {
      this.draggedItem = component;
      this.isDragging = true;
      event.dataTransfer.effectAllowed = 'copy';
      // Establecemos los datos que queremos transferir
      event.dataTransfer.setData('text/plain', JSON.stringify(component));
    },

    onDragEnd() {
      this.isDragging = false;
      this.draggedItem = null;
    },

    onDragEnter(event) {
      event.currentTarget.classList.add('dragover');
    },

    onDragLeave(event) {
      event.currentTarget.classList.remove('dragover');
    },

    onDrop(event) {
      event.currentTarget.classList.remove('dragover');
      try {
        const data = event.dataTransfer.getData('text/plain');
        const component = JSON.parse(data);
        const newComponent = this.cloneComponent(component);
        this.template.push(newComponent);
        this.saveToHistory();
        
        // Forzar actualización reactiva
        this.$forceUpdate();
      } catch (error) {
        console.error('Error al procesar el componente:', error);
      }
    },

    cloneComponent(component) {
      return {
        ...component,
        id: `${component.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        content: component.content || '',
        props: { ...component.props }
      };
    },

    editBlock(index) {
      this.currentBlockIndex = index
      this.currentBlock = JSON.parse(JSON.stringify(this.template[index]))
      this.showEditModal = true
    },

    duplicateBlock(index) {
      const block = { ...this.template[index] }
      block.id = `${block.id}_${Date.now()}`
      this.template.splice(index + 1, 0, block)
    },

    removeBlock(index) {
      this.template.splice(index, 1)
    },

    updateBlockContent(content) {
      if (this.currentBlock) {
        this.currentBlock.content = content
      }
    },

    saveChanges() {
      // Implementar lógica de guardado si es necesario
      this.closeModal()
    },

    closeModal() {
      this.showEditModal = false
      this.currentBlock = null
      this.currentBlockIndex = null
    },

    previewEmail() {
      const previewWindow = window.open('', '_blank')
      previewWindow.document.write(this.generateHTML())
      previewWindow.document.close()
    },

    exportHTML() {
      const htmlContent = this.generateHTML()
      const blob = new Blob([htmlContent], { type: 'text/html' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'email-template.html'
      a.click()
      window.URL.revokeObjectURL(url)
    },

    saveBlockChanges(changes) {
      if (this.currentBlockIndex !== null && this.currentBlock) {
        const updatedTemplate = [...this.template]
        
        if (this.currentBlock.type === 'ButtonBlock') {
          // Para botones, actualizamos la estructura completa
          updatedTemplate[this.currentBlockIndex] = {
            ...this.currentBlock,
            content: changes.content
          }
        } else {
          // Para otros tipos de bloques
          updatedTemplate[this.currentBlockIndex] = {
            ...this.currentBlock,
            content: changes.content || this.currentBlock.content,
            props: {
              ...this.currentBlock.props,
              ...changes.styles
            }
          }
        }

        this.template = updatedTemplate
        this.saveToHistory()
      }
      this.closeModal()
    },

    // =================== NUEVAS FUNCIONALIDADES ===================

    // History Management (Undo/Redo)
    saveToHistory() {
      const state = JSON.stringify(this.template)
      
      // Remover estados futuros si estamos en medio del historial
      this.history = this.history.slice(0, this.historyIndex + 1)
      
      // Agregar nuevo estado
      this.history.push(state)
      this.historyIndex = this.history.length - 1
      
      // Mantener el tamaño máximo del historial
      if (this.history.length > this.maxHistorySize) {
        this.history.shift()
        this.historyIndex--
      }
    },

    undoAction() {
      if (this.canUndo) {
        this.historyIndex--
        this.template = JSON.parse(this.history[this.historyIndex])
      }
    },

    redoAction() {
      if (this.canRedo) {
        this.historyIndex++
        this.template = JSON.parse(this.history[this.historyIndex])
      }
    },

    // Block Management
    selectBlock(index) {
      this.selectedBlockIndex = index
    },

    getBlockDisplayName(type) {
      const names = {
        TextBlock: 'Texto',
        ImageBlock: 'Imagen',
        ButtonBlock: 'Botón',
        SpacerBlock: 'Espaciador',
        DividerBlock: 'Divisor',
        ColumnBlock: 'Columnas',
        SocialBlock: 'Redes Sociales',
        TableBlock: 'Tabla',
        VideoBlock: 'Video'
      }
      return names[type] || type
    },

    moveBlockUp(index) {
      if (index > 0) {
        const temp = this.template[index]
        this.$set(this.template, index, this.template[index - 1])
        this.$set(this.template, index - 1, temp)
        this.selectedBlockIndex = index - 1
        this.saveToHistory()
      }
    },

    moveBlockDown(index) {
      if (index < this.template.length - 1) {
        const temp = this.template[index]
        this.$set(this.template, index, this.template[index + 1])
        this.$set(this.template, index + 1, temp)
        this.selectedBlockIndex = index + 1
        this.saveToHistory()
      }
    },

    onDropAtPosition(event, position) {
      try {
        const data = event.dataTransfer.getData('text/plain')
        const component = JSON.parse(data)
        const newComponent = this.cloneComponent(component)
        
        this.template.splice(position, 0, newComponent)
        this.saveToHistory()
        
        // Forzar actualización reactiva
        this.$forceUpdate()
      } catch (error) {
        console.error('Error al insertar componente:', error)
      }
    },

    // Template Management
    async loadTemplates() {
      this.availableTemplates = TemplateService.getAllTemplates()
    },

    async loadTemplate(template) {
      if (confirm('¿Estás seguro de que quieres cargar esta plantilla? Se perderá el trabajo actual.')) {
        this.template = JSON.parse(JSON.stringify(template.content))
        this.projectName = `${template.name} - Copia`
        this.saveToHistory()
        this.$toast.success('Plantilla cargada exitosamente')
      }
    },

    async saveAsTemplate() {
      if (this.template.length === 0) {
        this.$toast.error('No hay contenido para guardar como plantilla')
        return
      }

      const templateName = prompt('Nombre de la plantilla:')
      if (!templateName) return

      const templateData = {
        name: templateName,
        description: prompt('Descripción (opcional):') || '',
        content: this.template,
        category: 'custom',
        tags: []
      }

      const result = TemplateService.saveTemplate(templateData)
      if (result.success) {
        this.loadTemplates()
        this.$toast.success(result.message)
      } else {
        this.$toast.error(result.message)
      }
    },

    // Settings Management
    addVariable() {
      this.customVariables.push({ name: '', value: '' })
    },

    removeVariable(index) {
      this.customVariables.splice(index, 1)
    },

    // Enhanced HTML Generation
    generateHTML() {
      const styles = `
        <style>
          body { 
            margin: 0; 
            padding: 0; 
            font-family: ${this.emailSettings.fontFamily}; 
            background-color: ${this.emailSettings.backgroundColor};
            -webkit-text-size-adjust: 100%;
            -ms-text-size-adjust: 100%;
          }
          .email-container { 
            max-width: ${this.emailSettings.width}; 
            margin: 0 auto; 
            padding: 20px;
            background-color: #ffffff;
          }
          /* Responsive styles */
          @media only screen and (max-width: 600px) {
            .email-container {
              width: 100% !important;
              padding: 10px !important;
            }
          }
          /* Client-specific styles */
          .ExternalClass { width: 100%; }
          .ExternalClass, .ExternalClass p, .ExternalClass span, .ExternalClass font, .ExternalClass td, .ExternalClass div {
            line-height: 100%;
          }
          #outlook a { padding: 0; }
          table { border-collapse: collapse; }
        </style>
      `
      
      let content = this.template.map(block => {
        let html = ''
        
        switch(block.type) {
          case 'TextBlock':
            html = `<div style="margin: 10px 0; font-size: ${block.props.fontSize}; color: ${block.props.color};">${this.processVariables(block.content)}</div>`
            break
          case 'ImageBlock':
            html = `<div style="text-align: ${block.props.align || 'center'}; margin: 10px 0;">
              <img src="${block.content}" alt="${block.props.alt}" style="max-width: ${block.props.width}; height: auto; display: block; margin: 0 auto;">
            </div>`
            break
          case 'ButtonBlock':
            const btnContent = typeof block.content === 'object' ? block.content : { text: block.content, url: '#', styles: block.props }
            html = `
              <div style="text-align: ${btnContent.styles?.align || 'center'}; margin: 20px 0;">
                <a href="${btnContent.url || '#'}" style="
                  background-color: ${btnContent.styles?.backgroundColor || '#007bff'}; 
                  color: ${btnContent.styles?.color || '#ffffff'}; 
                  padding: 12px 24px; 
                  text-decoration: none; 
                  border-radius: 4px;
                  display: inline-block;
                  font-weight: 500;
                  border: none;
                ">
                  ${this.processVariables(btnContent.text || 'Botón')}
                </a>
              </div>
            `
            break
          case 'SpacerBlock':
            html = `<div style="height: ${block.props.height || '20px'};"></div>`
            break
          case 'DividerBlock':
            html = `<hr style="border: 0; border-top: 1px ${block.props.style || 'solid'} ${block.props.color || '#dddddd'}; margin: 10px 0;">`
            break
          case 'ColumnBlock':
            const columns = block.content || []
            const columnWidth = Math.floor(100 / (block.props.columns || 2))
            html = `
              <table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
                <tr>
                  ${columns.map(col => `
                    <td style="width: ${columnWidth}%; vertical-align: top; padding: ${block.props.gap || '10px'};">
                      ${this.processVariables(col.content || '')}
                    </td>
                  `).join('')}
                </tr>
              </table>
            `
            break
          case 'SocialBlock':
            const socialLinks = block.props.socialLinks || []
            html = `
              <div style="text-align: ${block.props.align || 'center'}; margin: 20px 0;">
                ${block.props.title ? `<h3 style="color: ${block.props.titleColor}; margin-bottom: 15px;">${block.props.title}</h3>` : ''}
                <div>
                  ${socialLinks.map(social => `
                    <a href="${social.url}" style="
                      display: inline-block;
                      margin: 0 ${block.props.iconSpacing || '8px'};
                      width: 40px;
                      height: 40px;
                      border-radius: 50%;
                      background-color: ${social.backgroundColor || '#ffffff'};
                      border: 2px solid ${social.color};
                      text-align: center;
                      line-height: 36px;
                      color: ${social.color};
                      text-decoration: none;
                    ">
                      <span style="font-family: FontAwesome; font-size: ${block.props.iconSize || '18px'};">${this.getIconUnicode(social.icon)}</span>
                    </a>
                  `).join('')}
                </div>
              </div>
            `
            break
          case 'TableBlock':
            const headers = block.props.headers || []
            const rows = block.props.rows || []
            html = `
              <table style="width: 100%; border-collapse: collapse; margin: 20px 0; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                ${block.props.showHeader ? `
                  <thead>
                    <tr>
                      ${headers.map(header => `
                        <th style="
                          background-color: ${block.props.headerBackgroundColor || '#007bff'};
                          color: ${block.props.headerTextColor || '#ffffff'};
                          padding: ${block.props.cellPadding || '12px'};
                          text-align: left;
                          border: 1px solid ${block.props.borderColor || '#dee2e6'};
                        ">${header}</th>
                      `).join('')}
                    </tr>
                  </thead>
                ` : ''}
                <tbody>
                  ${rows.map((row, index) => `
                    <tr style="background-color: ${index % 2 === 0 ? block.props.rowBackgroundColor || '#ffffff' : block.props.alternateRowColor || '#f8f9fa'};">
                      ${row.map(cell => `
                        <td style="
                          padding: ${block.props.cellPadding || '12px'};
                          border: 1px solid ${block.props.borderColor || '#dee2e6'};
                          color: ${block.props.textColor || '#2c3e50'};
                        ">${this.processVariables(cell)}</td>
                      `).join('')}
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            `
            break
          case 'VideoBlock':
            html = `
              <div style="text-align: ${block.props.align || 'center'}; margin: 20px 0;">
                <a href="${block.props.videoUrl || '#'}" style="text-decoration: none; color: inherit;">
                  <div style="position: relative; display: inline-block; max-width: ${block.props.maxWidth || '600px'}; width: 100%;">
                    <img src="${block.props.thumbnailUrl || block.content}" alt="${block.props.altText || 'Video'}" style="width: 100%; height: auto; border-radius: ${block.props.borderRadius || '8px'};">
                    <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 80px; height: 80px; background-color: rgba(0,0,0,0.7); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                      <span style="color: white; font-size: 32px; margin-left: 4px;">▶</span>
                    </div>
                  </div>
                  ${block.props.title ? `<h3 style="color: ${block.props.titleColor}; margin: 15px 0 8px 0;">${block.props.title}</h3>` : ''}
                  ${block.props.description ? `<p style="color: ${block.props.descriptionColor}; margin: 0;">${block.props.description}</p>` : ''}
                </a>
              </div>
            `
            break
          default:
            html = `<div>Tipo de bloque no soportado: ${block.type}</div>`
        }
        
        return html
      }).join('')

      return `
        <!DOCTYPE html>
        <html xmlns="http://www.w3.org/1999/xhtml">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <meta http-equiv="X-UA-Compatible" content="IE=edge">
          <title>${this.projectName || 'Email'}</title>
          ${styles}
        </head>
        <body>
          <div class="email-container">
            ${content}
          </div>
        </body>
        </html>
      `
    },

    processVariables(content) {
      let processed = content
      this.customVariables.forEach(variable => {
        const regex = new RegExp(`{${variable.name}}`, 'g')
        processed = processed.replace(regex, variable.value)
      })
      return processed
    },

    getIconUnicode(iconClass) {
      // Mapeo simplificado de iconos FontAwesome a Unicode
      const iconMap = {
        'fab fa-facebook-f': '📘',
        'fab fa-twitter': '🐦',
        'fab fa-instagram': '📷',
        'fab fa-linkedin-in': '💼'
             }
       return iconMap[iconClass] || '📱'
     },



     // Keyboard shortcuts
     handleKeyboard(event) {
       if (event.ctrlKey || event.metaKey) {
         switch(event.key) {
           case 'z':
             event.preventDefault()
             if (event.shiftKey) {
               this.redoAction()
             } else {
               this.undoAction()
             }
             break
           case 'y':
             event.preventDefault()
             this.redoAction()
             break
           case 's':
             event.preventDefault()
             this.saveAsTemplate()
             break
         }
       }
     }
  }
}
</script>

<style scoped>
.email-builder {
  display: flex;
  height: 100vh;
  background-color: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ===== SIDEBAR PANEL ===== */
.sidebar-panel {
  width: 320px;
  flex-shrink: 0;
  background: white;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.sidebar-tabs {
  display: flex;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.tab-btn {
  flex: 1;
  padding: 12px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
}

.tab-btn.active {
  background: white;
  color: #3b82f6;
  border-bottom: 2px solid #3b82f6;
}

.tab-btn:hover {
  background: rgba(59, 130, 246, 0.1);
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

/* Components Panel */
.search-box {
  position: relative;
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: #f8fafc;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

.components-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}

.category-btn {
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
}

.category-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.components-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.component-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: move;
  transition: all 0.2s ease;
}

.component-item:hover {
  background: #e0f2fe;
  border-color: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.component-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
}

.component-info {
  flex: 1;
  min-width: 0;
}

.component-name {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: #1e293b;
  margin-bottom: 2px;
}

.component-desc {
  display: block;
  font-size: 11px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== MAIN WORKSPACE ===== */
.main-workspace {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Top Toolbar */
.top-toolbar {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.project-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.btn-edit-name {
  padding: 6px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-edit-name:hover {
  background: #f1f5f9;
  color: #3b82f6;
}

.toolbar-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.view-mode-selector {
  display: flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
}

.view-btn {
  padding: 8px 12px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
}

.view-btn.active {
  background: white;
  color: #3b82f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toolbar-right {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-action {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
}

.btn-action:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #3b82f6;
  color: #3b82f6;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-preview {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-export {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-send {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-preview:hover,
.btn-export:hover,
.btn-send:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.divider {
  width: 1px;
  height: 24px;
  background: #e2e8f0;
  margin: 0 8px;
}

/* Design Container */
.design-container {
  flex: 1;
  background: #f8fafc;
  overflow: auto;
  padding: 24px;
}

.design-wrapper {
  display: flex;
  justify-content: center;
  min-height: 100%;
}

.design-canvas {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  min-height: 800px;
  position: relative;
  transition: all 0.3s ease;
}

.view-desktop .design-canvas {
  width: 800px;
  max-width: 800px;
}

.view-tablet .design-canvas {
  width: 768px;
  max-width: 768px;
}

.view-mobile .design-canvas {
  width: 375px;
  max-width: 375px;
}

/* Email Canvas Content */
.email-bounds-indicator {
  padding: 12px 24px;
  background: #f8fafc;
  border-bottom: 2px dashed #e2e8f0;
  text-align: center;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.email-bounds-indicator.email-end {
  border-bottom: none;
  border-top: 2px dashed #e2e8f0;
}

.empty-canvas {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px;
}

.empty-message {
  text-align: center;
  color: #64748b;
}

.empty-message i {
  font-size: 48px;
  color: #cbd5e1;
  margin-bottom: 16px;
}

.empty-message h3 {
  margin: 0 0 8px 0;
  color: #374151;
  font-weight: 600;
}

.empty-message p {
  margin: 0;
  font-size: 14px;
}

.blocks-container {
  padding: 24px;
  min-height: 400px;
}

.design-block {
  position: relative;
  margin: 12px 0;
  border-radius: 8px;
  transition: all 0.2s ease;
  group: block;
}

.design-block.block-selected {
  box-shadow: 0 0 0 2px #3b82f6;
}

.design-block.block-hover {
  box-shadow: 0 0 0 1px #93c5fd;
}

.block-toolbar {
  position: absolute;
  top: -40px;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px 6px 0 0;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 8px;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.block-handle {
  display: flex;
  align-items: center;
  color: #64748b;
  cursor: move;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.block-handle:hover {
  background: #f1f5f9;
  color: #3b82f6;
}

.block-info {
  flex: 1;
  min-width: 0;
}

.block-type {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.block-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.block-actions .btn-action {
  padding: 6px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 12px;
}

.block-actions .btn-action:hover {
  background: #f1f5f9;
  color: #3b82f6;
}

.block-actions .btn-delete:hover {
  background: #fef2f2;
  color: #ef4444;
}

.block-content {
  position: relative;
  border: 2px solid transparent;
  border-radius: 6px;
  transition: all 0.2s ease;
  overflow: hidden;
}

.design-block.block-selected .block-content {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.02);
}

.design-block.block-hover .block-content {
  border-color: #93c5fd;
}

.insert-area {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
}

.insert-area.insert-above {
  margin-top: -20px;
  margin-bottom: -20px;
}

.insert-area.insert-bottom {
  margin-top: 20px;
  margin-bottom: 20px;
}

.insert-area:hover {
  opacity: 1;
}

.insert-indicator {
  background: #3b82f6;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.block-ghost {
  opacity: 0.5;
  background: #f1f5f9;
  border: 2px dashed #93c5fd;
}

/* Drag and Drop States */
.design-canvas.dragover {
  background: rgba(59, 130, 246, 0.05);
}

.design-canvas.dragover::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px dashed #3b82f6;
  border-radius: 8px;
  pointer-events: none;
}

/* Templates Panel */
.templates-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.templates-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.templates-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.templates-actions {
  display: flex;
  gap: 8px;
}

.btn-save-template,
.btn-load-template {
  padding: 6px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
}

.btn-save-template:hover,
.btn-load-template:hover {
  background: #f8fafc;
  border-color: #3b82f6;
  color: #3b82f6;
}

.template-search {
  margin-bottom: 16px;
}

.template-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}

.templates-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  flex: 1;
  overflow-y: auto;
}

.template-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.template-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.template-thumbnail {
  position: relative;
  width: 100%;
  height: 120px;
  overflow: hidden;
  background: #f8fafc;
}

.template-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.template-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
}

.template-card:hover .template-overlay {
  opacity: 1;
}

.btn-use-template {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.template-info {
  padding: 12px;
}

.template-info h4 {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.template-info p {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Settings Panel */
.settings-panel h3 {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.setting-group {
  margin-bottom: 20px;
}

.setting-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.setting-select,
.setting-color {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: all 0.2s ease;
}

.setting-select:focus,
.setting-color:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.variables-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.variable-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.variable-input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 12px;
}

.btn-remove {
  padding: 6px;
  border: none;
  background: #fef2f2;
  color: #ef4444;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-remove:hover {
  background: #fee2e2;
}

.btn-add-variable {
  padding: 8px 12px;
  border: 1px dashed #e2e8f0;
  background: transparent;
  color: #64748b;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.btn-add-variable:hover {
  border-color: #3b82f6;
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .sidebar-panel {
    width: 280px;
  }
  
  .view-desktop .design-canvas {
    width: 90%;
    max-width: 700px;
  }
}

@media (max-width: 768px) {
  .email-builder {
    flex-direction: column;
  }

  .sidebar-panel {
    width: 100%;
    height: 300px;
    order: 2;
  }

  .main-workspace {
    order: 1;
  }

  .top-toolbar {
    padding: 8px 16px;
    min-height: 56px;
  }

  .toolbar-center {
    display: none;
  }

  .project-title {
    font-size: 16px;
  }

  .design-container {
    padding: 16px;
  }

  .design-canvas {
    width: 100% !important;
    max-width: 100% !important;
  }

  .components-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 8px;
  }

  .component-item {
    flex-direction: column;
    text-align: center;
    padding: 8px;
  }

  .component-icon {
    width: 28px;
    height: 28px;
  }

  .component-name {
    font-size: 11px;
  }

  .component-desc {
    display: none;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .email-builder {
    background-color: #0f172a;
  }
  
  .sidebar-panel {
    background: #1e293b;
    border-color: #334155;
  }
  
  .top-toolbar {
    background: #1e293b;
    border-color: #334155;
  }
  
  .design-canvas {
    background: #1e293b;
  }
}
</style>